import axios from 'axios'
import {
    useQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import { PowerElectricityType, type PowerElectricity, type PowerElectricityFormInput } from '../types'
import { convertToKiloWattHoursPrice } from '../helpers'

export function usePowerElectricities(estateId) {
    return useQuery({
        queryKey: ['powerElectricities-' + estateId],
        queryFn: async () => {
            const { data } : { data : Array<PowerElectricity> } = await axios.get(
                '/api/power_electricity/list?estate_id=' + estateId,
            )
            return data ? data.sort((a,b)=> a.id - b.id): data // backend doesn't sort the data
        },
    })
}

export function usePowerElectricity(powerElectricityId) {
    return useQuery({
        queryKey: ['powerElectricity-' + powerElectricityId],
        queryFn: async () => {
            const { data } : { data : PowerElectricity } = await axios.get(
                '/api/power_electricity/single?id=' + powerElectricityId,
            )
            return data
        },
    })
}

export const useCreatePowerElectricity = (estateId, returnToList: (
    (() => void) |
    ((powerElectricityId: number) => void))) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: PowerElectricityFormInput) => {            
            const queryParams = new URLSearchParams()
            queryParams.append('estate_id', estateId)
            if (data.type) {
                queryParams.append('type', data.type)
            }
            if (data.type == PowerElectricityType.spot && data.power_fees_amount_cents != null) {
                queryParams.append('power_fees_amount_cents', convertToKiloWattHoursPrice(data.power_fees_amount_cents).toString())
            }
            if (data.type == PowerElectricityType.flat && data.power_electricity_amount_cents != null) {
                queryParams.append('power_electricity_amount_cents', convertToKiloWattHoursPrice(data.power_electricity_amount_cents).toString())
            }
            return axios.get(`/api/power_electricity/create?${queryParams.toString()}`)
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['powerElectricities-' + estateId] })
            returnToList(response.data.id)
        },
    })
}

export const useUpdatePowerElectricity = (powerElectricityId, estateId, handleClose) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: PowerElectricityFormInput) => {
            const queryParams = new URLSearchParams()
            queryParams.append('id', powerElectricityId)
            if (data.type) {
                queryParams.append('type', data.type)
            }
            if (data.type == PowerElectricityType.spot && data.power_fees_amount_cents != null) {
                queryParams.append('power_fees_amount_cents', convertToKiloWattHoursPrice(data.power_fees_amount_cents).toString())
            }
            if (data.type == PowerElectricityType.flat && data.power_electricity_amount_cents != null) {
                queryParams.append('power_electricity_amount_cents', convertToKiloWattHoursPrice(data.power_electricity_amount_cents).toString())
            }
            return axios.get(`/api/power_electricity/update?${queryParams.toString()}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['powerElectricities-' + estateId] })
            queryClient.invalidateQueries({ queryKey: ['powerElectricity-' + powerElectricityId] })
            handleClose()
        },
    })
}
